import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { masterSchemeList } from "../../api/bseApi";

const BSE_PAGE_SIZE = 10;

export default function MutualFundComparison() {
  const [activeTab, setActiveTab] = useState("top-funds");

  const topFundManagers = [
    {
      id: 1,
      name: "Prashant Jain",
      fundHouse: "HDFC Mutual Fund",
      experience: "25+ years",
      fundsManaged: 8,
      totalAUM: "₹45,678 Cr",
      avgReturns: "18.5%",
      specialization: "Large Cap, Balanced",
    },
    {
      id: 2,
      name: "Neelesh Surana",
      fundHouse: "Mirae Asset Mutual Fund",
      experience: "18+ years",
      fundsManaged: 6,
      totalAUM: "₹32,456 Cr",
      avgReturns: "22.3%",
      specialization: "Mid Cap, Small Cap",
    },
    {
      id: 3,
      name: "Sankaran Naren",
      fundHouse: "ICICI Prudential Mutual Fund",
      experience: "22+ years",
      fundsManaged: 12,
      totalAUM: "₹38,945 Cr",
      avgReturns: "16.8%",
      specialization: "Multi Cap, Debt",
    },
    {
      id: 4,
      name: "Shreyash Devalkar",
      fundHouse: "Axis Mutual Fund",
      experience: "15+ years",
      fundsManaged: 5,
      totalAUM: "₹28,234 Cr",
      avgReturns: "18.5%",
      specialization: "Large Cap, Technology",
    },
    {
      id: 5,
      name: "R. Srinivasan",
      fundHouse: "SBI Mutual Fund",
      experience: "20+ years",
      fundsManaged: 7,
      totalAUM: "₹22,156 Cr",
      avgReturns: "19.2%",
      specialization: "Small Cap, Mid Cap",
    },
  ];

  const topInvestors = [
    {
      id: 1,
      name: "Rakesh Jhunjhunwala",
      portfolio: "₹25,000 Cr",
      topHoldings: ["Tata Motors", "Titan Company", "Crisil"],
      investmentStyle: "Value Investing",
      experience: "35+ years",
    },
    {
      id: 2,
      name: "Radhakishan Damani",
      portfolio: "₹18,500 Cr",
      topHoldings: ["Avenue Supermarts", "VST Industries", "India Cements"],
      investmentStyle: "Long-term Value",
      experience: "30+ years",
    },
    {
      id: 3,
      name: "Ramesh Damani",
      portfolio: "₹12,300 Cr",
      topHoldings: ["Tata Consultancy", "Infosys", "HDFC Bank"],
      investmentStyle: "Growth Investing",
      experience: "28+ years",
    },
    {
      id: 4,
      name: "Dolly Khanna",
      portfolio: "₹8,900 Cr",
      topHoldings: ["Rain Industries", "KCP", "NCC"],
      investmentStyle: "Mid-cap Focus",
      experience: "20+ years",
    },
    {
      id: 5,
      name: "Ashish Kacholia",
      portfolio: "₹6,700 Cr",
      topHoldings: [
        "Honeywell Automation",
        "Balkrishna Industries",
        "Aarti Industries",
      ],
      investmentStyle: "Small-cap Specialist",
      experience: "15+ years",
    },
  ];

  const [data, setData] = useState([]);
  const [categories, setCategories] = useState([
    { id: "all", name: "All Categories" },
  ]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [bseSearch, setBseSearch] = useState("");
  const [bseCurrentPage, setBseCurrentPage] = useState(1);
  const [bseTotal, setBseTotal] = useState(0);
  const [bseLoading, setBseLoading] = useState(false);
  const [bseError, setBseError] = useState("");
  const bseDebounceRef = useRef(null);

  const fetchBseSchemes = useCallback(async () => {
    setBseLoading(true);
    setBseError("");
    try {
      const payload = {
        data: {
          fields: ["ALL"],
          count_only: false,
          start: (bseCurrentPage - 1) * BSE_PAGE_SIZE,
          length: BSE_PAGE_SIZE,
          search: { value: bseSearch.trim() },
          filter_param: {
            scheme_amc_name: "",
            scheme_category:
              selectedCategory && selectedCategory !== "all"
                ? selectedCategory
                : "",
            scheme_sub_category: "",
            scheme_option: "",
            scheme_plan: "",
            scheme_isin: "",
            settlement_days: "",
          },
        },
      };

      const res = await masterSchemeList(payload);
      const body = res?.data || {};
      const lists = Array.isArray(body?.data?.lists) ? body.data.lists : [];
      const count = Number(body?.data?.count ?? lists.length) || lists.length;

      const mapped = lists.map((scheme, idx) => ({
        id:
          scheme.scheme_isin ||
          scheme.scheme_bse_code ||
          scheme.scheme_amfi_code ||
          `${scheme.name}-${idx}`,
        schemes: scheme.name || scheme.parent_scheme_name || "—",
        category: scheme.scheme_category || "—",
        sub_category: scheme.scheme_sub_category || "—",
        amc_name: scheme.scheme_amc_name || "—",
        scheme_plan: scheme.scheme_plan || "—",
        scheme_option: scheme.scheme_option || "—",
        scheme_isin: scheme.scheme_isin || "",
        scheme_bse_code: scheme.scheme_bse_code || "",
        is_active: !!scheme.is_active,
        sip_flag: !!scheme.sip_flag,
      }));

      setData(mapped);
      setBseTotal(count);

      const uniqueCategories = Array.from(
        new Set(mapped.map((f) => f.category).filter((c) => c && c !== "—"))
      );
      setCategories((prev) => {
        const existing = new Set(prev.map((c) => c.id));
        const additions = uniqueCategories
          .filter((c) => !existing.has(c))
          .map((c) => ({ id: c, name: c }));
        return additions.length ? [...prev, ...additions] : prev;
      });
    } catch (err) {
      const apiMsg =
        err?.response?.data?.message ||
        err?.response?.data?.data?.messages?.[0]?.errcode ||
        err?.message ||
        "Failed to fetch BSE schemes";
      setBseError(String(apiMsg));
      setData([]);
      setBseTotal(0);
    } finally {
      setBseLoading(false);
    }
  }, [bseCurrentPage, bseSearch, selectedCategory]);

  useEffect(() => {
    if (activeTab !== "top-funds") return undefined;
    if (bseDebounceRef.current) clearTimeout(bseDebounceRef.current);
    bseDebounceRef.current = setTimeout(() => {
      fetchBseSchemes();
    }, 350);
    return () => {
      if (bseDebounceRef.current) clearTimeout(bseDebounceRef.current);
    };
  }, [fetchBseSchemes, activeTab]);

  useEffect(() => {
    setBseCurrentPage(1);
  }, [bseSearch, selectedCategory]);

  const filteredData = data;
  const bseTotalPages = Math.max(1, Math.ceil(bseTotal / BSE_PAGE_SIZE));

  return (
    <>
      <style>{`

    @media (max-width: 768px) {
    h1.text-4xl.font-bold.mb-4 {
    font-size: 23px;
}
    h2.text-3xl.font-bold.text-gray-900.mb-4 {
    font-size: 23px;
}
    h2.text-4xl.font-bold.mb-4 {
    font-size: 23px;
}
a.btn.btn-primary.my-4 {
    margin: 0 0 !important;
}


.text-center {
    text-align: none !important;
}
    section.relative.text-gray-800.py-16 {
    padding-top: 20px;
}
    p.text-xl.mb-8.max-w-3xl.mx-auto.text-gray-700 {
    margin: 0;
}
    .flex.flex-wrap.justify-center.gap-3.mb-8 {
    margin: 0;
}
    }
  `}</style>

      {/* Hero Section */}
      <section
        className="relative text-gray-800 py-16"
        style={{
          background:
            "linear-gradient(135deg, rgb(240 253 253) 0%, rgb(155 222 218 / 73%) 50%, rgb(3 186 187) 100%",
        }}
      >
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(255,255,255,0.3) 0%, transparent 100%)",
          }}
        ></div>
        <div className="relative max-w-6xl mx-auto px-4 text-center pt-4">
          <h1 className="text-4xl font-bold mb-4" style={{ color: "#1bafb4" }}>
            Mutual Fund Comparison & Analysis
          </h1>
          <p className="text-xl mb-8 max-w-3xl mx-auto text-gray-700">
            Compare top-performing mutual funds, discover leading fund managers,
            and learn from successful investors. Make informed investment
            decisions with our comprehensive analysis.
          </p>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => setActiveTab("top-funds")}
              onMouseEnter={(e) => (e.target.style.color = "#374151")}
              onMouseLeave={
                (e) =>
                (e.target.style.color =
                  activeTab === "top-funds" ? "" : "#374151") // gray-700 hex
              }
              className={`px-6 py-3 rounded-lg font-semibold transition duration-300 ${activeTab === "top-funds"
                ? "text-white shadow-lg"
                : "bg-gray-100 hover:bg-gray-200"
                }`}
              style={
                activeTab === "top-funds"
                  ? { background: "#1bafb4", color: "white" }
                  : { color: "#374151" }
              }
            >
              Top 5 Funds by Returns
            </button>

            <button
              onClick={() => setActiveTab("fund-managers")}
              onMouseEnter={(e) => (e.target.style.color = "#374151")}
              onMouseLeave={
                (e) =>
                (e.target.style.color =
                  activeTab === "fund-managers" ? "" : "#374151") // gray-700 hex
              }
              className={`px-6 py-3 rounded-lg font-semibold transition duration-300 ${activeTab === "fund-managers"
                ? "text-white shadow-lg"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              style={
                activeTab === "fund-managers"
                  ? { background: "#1bafb4", color: "white" }
                  : { color: "#374151" }
              }
            >
              Top 5 Fund Managers
            </button>
            <button
              onClick={() => setActiveTab("top-investors")}
              onMouseEnter={(e) => (e.target.style.color = "#374151")}
              onMouseLeave={
                (e) =>
                (e.target.style.color =
                  activeTab === "top-investors" ? "" : "#374151") // gray-700 hex
              }
              className={`px-6 py-3 rounded-lg font-semibold transition duration-300 ${activeTab === "top-investors"
                ? "text-white shadow-lg"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              style={
                activeTab === "top-investors"
                  ? { background: "#1bafb4", color: "white" }
                  : { color: "#374151" }
              }
            >
              Top Investors
            </button>
          </div>
        </div>
      </section>

      {/* Top Funds Section */}
      {activeTab === "top-funds" && (
        <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Mutual Fund Schemes
              </h2>
              <p className="text-lg text-gray-600">
                Live mutual fund scheme master from BSE STAR MF — search,
                filter and explore thousands of schemes
              </p>
            </div>

            {/* Search */}
            <div className="flex justify-center mb-6">
              <input
                type="text"
                value={bseSearch}
                onChange={(e) => setBseSearch(e.target.value)}
                placeholder="Search by scheme name..."
                className="w-full md:w-1/2 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none"
                style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.05)" }}
              />
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap justify-center gap-3 mb-6">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-6 py-3 rounded-lg text-sm font-medium transition duration-300 shadow-sm 
        ${selectedCategory === category.id
                      ? "text-white shadow-md"
                      : "bg-white text-gray-700 border border-gray-200"
                    }`}
                  style={
                    selectedCategory === category.id
                      ? { background: "#1bafb4" }
                      : {
                        transition: "all 0.3s ease",
                      }
                  }
                  onMouseEnter={(e) => {
                    if (selectedCategory !== category.id) {
                      e.currentTarget.style.backgroundColor = "#f3f4f6";
                      e.currentTarget.style.color = "#000";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedCategory !== category.id) {
                      e.currentTarget.style.backgroundColor = "#fff";
                      e.currentTarget.style.color = "#374151";
                    }
                  }}
                >
                  {category.name}
                </button>
              ))}
            </div>

            {/* Status line */}
            <div className="text-center text-sm text-gray-600 mb-3">
              {bseLoading
                ? "Loading schemes from BSE..."
                : `Showing ${filteredData.length} of ${bseTotal} schemes`}
              {bseError && (
                <span className="ml-2 text-red-600">Error: {bseError}</span>
              )}
            </div>

            <div className="overflow-x-auto mt-3">
              <table className="w-full border-collapse border border-gray-200 rounded-lg overflow-hidden shadow-lg">
                <thead className="text-white" style={{ background: "#1bafb4" }}>
                  <tr>
                    <th className="px-4 py-4 text-left w-1/3">Scheme Name</th>
                    <th className="px-2 py-4 text-left">AMC</th>
                    <th className="px-2 py-4 text-left">Category</th>
                    <th className="px-2 py-4 text-left">Sub Category</th>
                    <th className="px-2 py-4 text-left">Plan</th>
                    <th className="px-2 py-4 text-left">Option</th>
                  </tr>
                </thead>
                <tbody>
                  {!bseLoading && filteredData.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                        No schemes match your filters.
                      </td>
                    </tr>
                  )}
                  {filteredData.map((fund, index) => (
                    <tr
                      key={fund.id}
                      className={`${index % 2 === 0 ? "bg-white" : "bg-gray-50"
                        } hover:bg-cyan-50 transition duration-200`}
                    >
                      <td className="px-4 py-4 font-medium">
                        <div className="text-sm leading-tight">
                          {fund.schemes}
                        </div>
                        {fund.scheme_isin && (
                          <div className="text-xs text-gray-400 mt-1">
                            ISIN: {fund.scheme_isin}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-4 text-gray-700 text-sm">
                        {fund.amc_name}
                      </td>
                      <td className="px-2 py-4">
                        <div className="inline-block">
                          <span
                            className="px-3 py-1 rounded-lg text-xs font-medium whitespace-nowrap"
                            style={{ background: "#ccfbf1", color: "#1bafb4" }}
                          >
                            {fund.category}
                          </span>
                        </div>
                      </td>
                      <td className="px-2 py-4 text-gray-700 text-sm">
                        {fund.sub_category}
                      </td>
                      <td className="px-2 py-4 text-gray-700 text-sm">
                        {fund.scheme_plan}
                      </td>
                      <td className="px-2 py-4 text-gray-700 text-sm">
                        {fund.scheme_option}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {!bseLoading && bseTotalPages > 1 && (
              <div className="flex justify-center items-center gap-4 mt-6">
                <button
                  type="button"
                  disabled={bseCurrentPage === 1}
                  onClick={() => setBseCurrentPage((p) => Math.max(1, p - 1))}
                  className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 disabled:opacity-50"
                >
                  Prev
                </button>
                <span className="text-sm text-gray-600">
                  Page <strong>{bseCurrentPage}</strong> of {bseTotalPages}
                </span>
                <button
                  type="button"
                  disabled={bseCurrentPage === bseTotalPages}
                  onClick={() =>
                    setBseCurrentPage((p) => Math.min(bseTotalPages, p + 1))
                  }
                  className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Top Fund Managers Section */}
      {activeTab === "fund-managers" && (
        <section className="py-16 bg-gradient-to-br from-white to-blue-50">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Top 5 Fund Managers
              </h2>
              <p
                style={{ fontSize: "1.1rem", lineHeight: "1.8", color: "#000" }}
              >
                Meet the experts who manage your investments
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {topFundManagers.map((manager) => (
                <div
                  key={manager.id}
                  className="bg-gradient-to-br from-white to-gray-50 p-6 rounded-lg shadow-lg hover:shadow-xl transition duration-300 border border-gray-200"
                >
                  <div className="text-4xl mb-4">{manager.image}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {manager.name}
                  </h3>
                  <p
                    className=" font-medium mb-2"
                    style={{ color: "#183c6b", fontWeight: "600" }}
                  >
                    {manager.fundHouse}
                  </p>
                  <div className="space-y-2 text-sm text-gray-600">
                    <p
                      style={{
                        fontSize: "1.1rem",
                        lineHeight: "1.4",
                        color: "#000",
                      }}
                    >
                      <span className="font-medium">Experience:</span>{" "}
                      {manager.experience}
                    </p>
                    <p
                      style={{
                        fontSize: "1.1rem",
                        lineHeight: "1.4",
                        color: "#000",
                      }}
                    >
                      <span className="font-medium">Funds Managed:</span>{" "}
                      {manager.fundsManaged}
                    </p>
                    <p
                      style={{
                        fontSize: "1.1rem",
                        lineHeight: "1.4",
                        color: "#000",
                      }}
                    >
                      <span className="font-medium">Total AUM:</span>{" "}
                      {manager.totalAUM}
                    </p>
                    <p
                      style={{
                        fontSize: "1.1rem",
                        lineHeight: "1.4",
                        color: "#000",
                      }}
                    >
                      <span className="font-medium">Avg Returns:</span>{" "}
                      <span className="text-bleck-600 font-bold">
                        {manager.avgReturns}
                      </span>
                    </p>
                    <p
                      style={{
                        fontSize: "1.1rem",
                        lineHeight: "1.4",
                        color: "#000",
                      }}
                    >
                      <span className="font-medium">Specialization:</span>{" "}
                      {manager.specialization}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Top Investors Section */}
      {activeTab === "top-investors" && (
        <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Top Investors
              </h2>
              <p className="text-lg text-gray-600">
                Learn from the most successful investors in India
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {topInvestors.map((investor) => (
                <div
                  key={investor.id}
                  className="bg-gradient-to-br from-white to-gray-50 p-6 rounded-lg shadow-lg hover:shadow-xl transition duration-300 border border-gray-200"
                >
                  <div className="text-4xl mb-4">{investor.image}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {investor.name}
                  </h3>
                  <p
                    className=" font-bold text-lg mb-2"
                    style={{ color: "#183c6b", fontWeight: "600" }}
                  >
                    Portfolio: {investor.portfolio}
                  </p>
                  <div className="space-y-2 text-sm text-gray-600">
                    <p
                      style={{
                        fontSize: "1.1rem",
                        lineHeight: "1.4",
                        color: "#000",
                      }}
                    >
                      <span className="font-medium">Experience:</span>{" "}
                      {investor.experience}
                    </p>
                    <p
                      style={{
                        fontSize: "1.1rem",
                        lineHeight: "1.4",
                        color: "#000",
                      }}
                    >
                      <span className="font-medium">Investment Style:</span>{" "}
                      {investor.investmentStyle}
                    </p>
                    <p
                      style={{
                        fontSize: "1.1rem",
                        lineHeight: "1.4",
                        color: "#000",
                      }}
                    >
                      <span className="font-medium">Top Holdings:</span>
                    </p>
                    <ul
                      className="list-disc list-inside ml-2"
                      style={{ color: "#000" }}
                    >
                      {investor.topHoldings.map((holding, index) => (
                        <li key={index}>
                          <span className="text-green-500 mr-2">✓</span>
                          {holding}
                        </li>
                      ))}
                    </ul>

                    {/* <ul className="space-y-1">
                                    {card.front.features.slice(0, 3).map((feature, idx) => (
                                        <li key={idx} className="flex items-center text-sm text-gray-600">
                                            <span className="text-green-500 mr-2">✓</span>
                                            <span className="truncate">{feature}</span>
                                        </li>
                                         
                                    ))}
                                </ul> */}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section
        className="relative text-gray-800 py-16"
        style={{
          background:
            "linear-gradient(135deg, rgb(240 253 253) 0%, rgb(155 222 218 / 73%) 50%, rgb(3 186 187) 100%",
        }}
      >
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(255,255,255,0.3) 0%, transparent 100%)",
          }}
        ></div>
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4" style={{ color: "#1bafb4" }}>
            Ready to Start Investing?
          </h2>
          <p className="text-xl mb-8 text-gray-700">
            Based on our analysis, choose the best mutual funds for your
            portfolio. Get personalized recommendations from our expert
            advisors.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {/* <a
                            href="https://gpsfinvest.investwell.app/app/#/login"
                            className="text-white px-8 py-3 rounded-lg font-semibold transition duration-300 shadow-lg"
                            style={{ background: '#1bafb4' }}
                        >
                            Start Investing Now
                        </a> */}
            <Link
              to="/clientmutualfunds"
              className="btn btn-primary my-4"
              onClick={(e) => {
                const encryptedData = localStorage.getItem(
                  process.env.REACT_APP_LOGIN_DATA_STORE,
                );
                if (!encryptedData) {
                  e.preventDefault();
                  window.location.href = "https://gpsfinvest.investwell.app/app/#/login"; // redirect to login if not logged in
                } else {
                  window.scrollTo({ top: 0, behavior: "instant" });
                }
              }}
            >
              Start Investing Now
            </Link>
            {/* <button type="submit" className="btn btn-primary">
                        Start Investing Now
                                        </button> */}
            {/* <button type="submit" className="btn btn-primary " >
                            Get Expert Advice
                        </button> */}
            <Link to="/contact" className="btn btn-primary my-4" onClick={() =>
              window.scrollTo({ top: 0, behavior: "instant" })
            }>
              Get Expert Advice
            </Link>
            {/* <a
                            href="/contact"
                            className="text-gray-700 border-2 px-8 py-3 rounded-lg font-semibold transition duration-300 shadow-lg"
                            style={{ background: 'white', borderColor: '#1bafb4' }}
                        >
                            Get Expert Advice
                        </a> */}
          </div>
        </div>
      </section>
    </>
  );
}
